<template>
	<div class="cart-widget">
		<primary-button
			v-if="isTabOpened"
			class="cart-btn to-check-btn to-order mr-16"
			large
			:loading="toTabLoading"
			@click="handleGoToCheck"
		>
			<span class="cart-icon" />
			<span class="money">
				Current Order
			</span>
			<span class="empty" />
		</primary-button>
		<primary-button
			v-if="totalProductsCount"
			class="cart-btn"
			:loading="isOpenCartLoading"
			large
			@click="openCart"
		>
			<span class="cart-icon" />
			<span class="money">
				<formatted-price :price="totalPrice" decimal-height="70" />
			</span>
			<span class="circle">{{ totalProductsCount }}</span>
		</primary-button>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
// Utils
import storage from '@/utils/storage'
// Components
import PrimaryButton from '@/components/Buttons/PrimaryButton.vue'
// Mixins
import routerMixin from '@/mixins/routerMixin'
import FormattedPrice from '@/components/FormattedPrice.vue'

export default {
	name: 'Cart',

	mixins: [routerMixin],

	components: { FormattedPrice, PrimaryButton },

	data: () => ({
		toTabLoading: false,
		isOpenCartLoading: false
	}),

	computed: {
		...mapState('cart', ['cartItems', 'id']),
		...mapGetters('cart', ['totalPrice', 'totalProductsCount', 'isTabOpened'])
	},

	methods: {
		...mapActions('cart', ['getCart', 'toggleIsCartRecreated']),

		async getIsCartExpired() {
			const storageCartId = storage.getStorage('cartId')
			try {
				await this.getCart()
			} catch (error) {
				return
			}

			const isCartExpired = storageCartId !== this.id

			if (isCartExpired) {
				this.toggleIsCartRecreated()

				return true
			}
		},

		async handleGoToCheck() {
			this.toTabLoading = true
			const isCartExpired = await this.getIsCartExpired()

			if (isCartExpired) return

			await this.goToCheck()

			this.toTabLoading = false
		},

		async openCart() {
			this.isOpenCartLoading = true

			const isCartExpired = await this.getIsCartExpired()

			if (isCartExpired) {
				this.isOpenCartLoading = false

				return
			}

			const { query, params } = this.$route
			await this.$router.push({
				name: 'cart',
				query: { ...query, category: params.category }
			})

			this.isOpenCartLoading = false
		}
	}
}
</script>

<style lang="scss" scoped>
@import './src/assets/styles/variables';

.cart-widget {
	position: fixed;
	width: 100%;
	height: 90px;
	z-index: 99;
	bottom: 0;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	justify-content: center;
	padding: 0 8px;
	align-items: center;
	pointer-events: none;

	&::after {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: linear-gradient(
			0deg,
			rgba(0, 0, 0, 0.15) 0%,
			rgba(0, 0, 0, 0.025) 100%
		);
		backdrop-filter: blur(1px);
	}
}

.primary-button {
	z-index: 100;
}

.cart-btn {
	padding: 0 8px 0 24px;
	width: 304px;
	pointer-events: all;
	transition: background-color 0.3s;

	&:hover {
		background-color: $light-purple;
	}

	/deep/ .content {
		justify-content: space-between;
	}

	.cart-icon {
		display: block;
		width: 24px;
		height: 24px;
		color: #fff;
		background: url('../assets/images/ic_cart.svg') no-repeat 50%;
		cursor: pointer;
	}

	.money {
		display: block;
		white-space: nowrap;
		font-size: 20px;
		font-weight: 600;
	}

	.circle {
		min-width: 32px;
		min-height: 32px;
		padding: 5px;
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: rgba($blue-faceless-void, 0.7);
		border-radius: 50%;
		transform: scale(1);
		box-shadow: 0 0 0 0 rgba($blue-faceless-void, 1);
		font-size: 16px;
		font-weight: 600;
		line-height: 1.6;
		animation: pulse-green 2s infinite;
		aspect-ratio: 1 / 1;
	}

	&.to-order {
		.cart-icon {
			width: 24px;
			height: 30px;
			background: url('../assets/images/logo_embossed_white.svg') no-repeat 50%;
		}

		.empty {
			min-width: 40px;
		}

		.money {
			font-size: 16px;

			@media (min-width: 768px) {
				font-size: 20px;
			}
		}
	}

	@media (max-width: 767px) {
		padding: 0 8px 0 16px;

		.circle {
			min-width: 32px;
			padding: 6px 8px;
			font-size: 14px;
			line-height: 1.5;
		}
	}

	@media (max-width: 480px) {
		&.to-check-btn {
			margin-right: 8px;
			padding-right: 18px;
			padding-left: 12px;

			/deep/.content {
				.cart-icon {
					margin-right: 6px;
				}

				.empty {
					display: none;
				}

				.money {
					display: block;
					margin: 0 auto;
				}
			}
		}
	}
}
</style>
